// Take-two button primitive (UI-SPEC §5.1 / styles.css:132-159, ui.jsx:7-11).
//
// Wraps the `.btn` recipe from pieces.css: the take-two folk 3D press with the
// five canonical variants. All variant composition happens on the className;
// the underlying CSS handles the gradient, edge, press-sink, and disabled
// treatment.
//
// API decisions:
//   - `kind` defaults to 'primary' (the ember CTA semantic). The five variants
//     are the only ones the take-two contract allows: primary / gold (dark
//     ink) / them (frost) / leaf / ghost (flat card).
//   - `size` defaults to md, which carries NO modifier class (md is the base
//     recipe); only 'sm'/'lg' emit a class.
//   - `block` toggles full width (take-two `btn-block`).
//   - `disabled` maps to native HTML disabled (prevents onClick + the
//     `.btn[disabled]` fade/desaturate from pieces.css).
//   - `pressed` is consumer-controlled and only emits `aria-pressed` when
//     defined (so non-toggle buttons stay attribute-clean).
//   - `type` defaults to 'button' to avoid accidental form submission.

import type { CSSProperties, JSX, MouseEvent, ReactNode } from 'react'
import type { SfxCue } from '../../game/audio/cues.js'
import { playSfx } from '../../game/audio/useAudio.js'

export type BtnKind = 'primary' | 'gold' | 'them' | 'leaf' | 'ghost'
export type BtnSize = 'sm' | 'md' | 'lg'

const KIND_CLASS: Record<BtnKind, string> = {
  primary: 'btn-primary',
  gold: 'btn-gold',
  them: 'btn-them',
  leaf: 'btn-leaf',
  ghost: 'btn-ghost',
}

const SIZE_CLASS: Record<Exclude<BtnSize, 'md'>, string> = {
  sm: 'btn-sm',
  lg: 'btn-lg',
}

export interface BtnProps {
  readonly children: ReactNode
  readonly kind?: BtnKind
  readonly size?: BtnSize
  readonly block?: boolean
  readonly disabled?: boolean
  readonly pressed?: boolean
  readonly onClick?: (e: MouseEvent<HTMLButtonElement>) => void
  readonly style?: CSSProperties
  readonly ariaLabel?: string
  readonly type?: 'button' | 'submit'
  readonly testId?: string
  /** Analytics / e2e hook — emitted verbatim as `data-cta`. */
  readonly dataCta?: string
  /**
   * Tactile press cue (BrandIdentity §13 "soft tactile UI clicks"). Defaults
   * to 'ui-tap'; pass a different cue (e.g. 'ui-confirm' for a submit) or null
   * to silence a specific button. The audio engine no-ops when muted/locked,
   * so this is safe everywhere.
   */
  readonly sfx?: SfxCue | null
}

export function Btn({
  children,
  kind = 'primary',
  size = 'md',
  block = false,
  disabled = false,
  pressed,
  onClick,
  style,
  ariaLabel,
  type = 'button',
  testId,
  dataCta,
  sfx = 'ui-tap',
}: BtnProps): JSX.Element {
  const classes = [
    'btn',
    KIND_CLASS[kind],
    size === 'md' ? null : SIZE_CLASS[size],
    block ? 'btn-block' : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled}
      aria-label={ariaLabel}
      {...(pressed !== undefined ? { 'aria-pressed': pressed } : {})}
      onPointerDown={
        disabled || sfx === null
          ? undefined
          : () => {
              playSfx(sfx)
            }
      }
      // RMA11Y-03: keyboard activation (Enter/Space) fires `click` but NOT
      // `pointerdown`, so without this a keyboard user got the visual press but
      // no tactile cue. Pointer and keyboard input paths are mutually exclusive
      // for one activation (pointerdown is pointer-only; this is keyboard-only)
      // and neither plays on `click`, so there is exactly one cue per press —
      // no double-fire guard needed. `!e.repeat` silences held-key autorepeat.
      onKeyDown={
        disabled || sfx === null
          ? undefined
          : (e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) {
                playSfx(sfx)
              }
            }
      }
      onClick={onClick}
      style={style}
      data-testid={testId}
      data-cta={dataCta}
    >
      {children}
    </button>
  )
}
